import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import {
    assignCompletePlanToPatient,
    fetchPlanByUUID,
    getAllPlanTemplates
} from '../../../../services/plans';

const AssignPatientPlanDialog = ({
    open,
    onClose,
    patientUuid,
    patientName,
    onAssigned
}) => {
    const { t } = useTranslation();
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [startDate, setStartDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [isAssigning, setIsAssigning] = useState(false);
    const [assignmentError, setAssignmentError] = useState(false);

    const {
        data: planTemplates = [],
        isPending: plansLoading,
        isError: plansError
    } = useQuery({
        queryKey: ['patient-plan-assignment', 'templates'],
        queryFn: () => getAllPlanTemplates(),
        enabled: open,
        staleTime: 5 * 60 * 1000
    });

    const {
        data: selectedPlan,
        isPending: planLoading,
        isError: planError
    } = useQuery({
        queryKey: ['patient-plan-assignment', 'plan', selectedTemplate?.uuid],
        queryFn: () => fetchPlanByUUID(selectedTemplate.uuid),
        enabled: open && Boolean(selectedTemplate?.uuid)
    });

    useEffect(() => {
        if (open) return;
        setSelectedTemplate(null);
        setStartDate(dayjs().format('YYYY-MM-DD'));
        setAssignmentError(false);
    }, [open]);

    const durationWeeks = Number(selectedPlan?.data?.durationWeeks) || 0;
    const endDate = useMemo(
        () =>
            selectedPlan && startDate
                ? dayjs(startDate)
                      .add(durationWeeks, 'week')
                      .endOf('day')
                      .format('YYYY-MM-DD')
                : '',
        [durationWeeks, selectedPlan, startDate]
    );
    const questionnaireCount =
        (selectedPlan?.data?.initialQuestionnaires?.length || 0) +
        (selectedPlan?.data?.questionnaires?.length || 0);
    const learningContentCount =
        (selectedPlan?.data?.initialLearningContents?.length || 0) +
        (selectedPlan?.data?.learningContents?.length || 0);

    const handleClose = () => {
        if (!isAssigning) onClose();
    };

    const handleAssign = async () => {
        if (!selectedPlan || !startDate) return;

        setIsAssigning(true);
        setAssignmentError(false);

        try {
            await assignCompletePlanToPatient({
                plan: selectedPlan,
                patientUUID: patientUuid,
                startDate
            });
            await onAssigned();
            onClose();
        } catch {
            setAssignmentError(true);
        } finally {
            setIsAssigning(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {t('userPage.overview.assignments.planDialogTitle')}
            </DialogTitle>
            <DialogContent>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                    {t('userPage.overview.assignments.planDialogDescription', {
                        name: patientName
                    })}
                </Typography>

                {plansError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {t('userPage.overview.assignments.loadPlansError')}
                    </Alert>
                )}

                <Autocomplete
                    options={planTemplates}
                    value={selectedTemplate}
                    onChange={(_, value) => {
                        setSelectedTemplate(value);
                        setAssignmentError(false);
                    }}
                    getOptionLabel={(option) => option?.name || ''}
                    isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                    loading={plansLoading}
                    disabled={plansError || isAssigning}
                    noOptionsText={t('userPage.overview.assignments.noPlans')}
                    loadingText={t('userPage.overview.assignments.loading')}
                    renderOption={(props, option) => {
                        // React requires the option key to be passed separately from spread props.
                        // eslint-disable-next-line react/prop-types
                        const { key, ...optionProps } = props;
                        return (
                            <Box component="li" key={key} {...optionProps}>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography fontWeight={600}>
                                        {option.name}
                                    </Typography>
                                    {option.description && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            noWrap
                                        >
                                            {option.description}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        );
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t('userPage.overview.assignments.selectPlan')}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {plansLoading ? (
                                            <CircularProgress color="inherit" size={18} />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                )
                            }}
                        />
                    )}
                />

                {selectedTemplate && planLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {selectedTemplate && planError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {t('userPage.overview.assignments.loadPlanError')}
                    </Alert>
                )}

                {selectedPlan && (
                    <Stack spacing={2} sx={{ mt: 2.5 }}>
                        <Box>
                            <Typography fontWeight={700}>{selectedPlan.name}</Typography>
                            {selectedPlan.description && (
                                <Typography variant="body2" color="text.secondary">
                                    {selectedPlan.description}
                                </Typography>
                            )}
                            <Typography variant="body2" color="primary" sx={{ mt: 0.5 }}>
                                {t('userPage.overview.assignments.planDuration', {
                                    count: durationWeeks
                                })}
                            </Typography>
                        </Box>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                                type="date"
                                fullWidth
                                label={t('userPage.overview.assignments.startDate')}
                                value={startDate}
                                onChange={(event) => setStartDate(event.target.value)}
                                disabled={isAssigning}
                                InputLabelProps={{ shrink: true }}
                            />
                            <TextField
                                type="date"
                                fullWidth
                                label={t('userPage.overview.assignments.endDate')}
                                value={endDate}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ readOnly: true }}
                            />
                        </Stack>

                        <Divider />
                        <Box>
                            <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                                {t('userPage.overview.assignments.includedResources')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('userPage.overview.assignments.questionnairesCount', {
                                    count: questionnaireCount
                                })}
                                {' - '}
                                {t(
                                    'userPage.overview.assignments.learningContentsCount',
                                    {
                                        count: learningContentCount
                                    }
                                )}
                            </Typography>
                        </Box>
                    </Stack>
                )}

                {assignmentError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {t('userPage.overview.assignments.assignPlanError')}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={handleClose} disabled={isAssigning}>
                    {t('userPage.overview.assignments.cancel')}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleAssign}
                    disabled={!selectedPlan || !startDate || isAssigning}
                    startIcon={
                        isAssigning ? (
                            <CircularProgress color="inherit" size={16} />
                        ) : null
                    }
                >
                    {isAssigning
                        ? t('userPage.overview.assignments.assigningPlan')
                        : t('userPage.overview.assignments.assignPlan')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

AssignPatientPlanDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    patientUuid: PropTypes.string.isRequired,
    patientName: PropTypes.string,
    onAssigned: PropTypes.func.isRequired
};

export default AssignPatientPlanDialog;
